/**
 * AOS-PEO-03 — My Self-Service Portal
 *
 * Employee self-service resolved exclusively from the authenticated JWT.
 * No worker_id passed from frontend — all identity resolution is server-side.
 *
 * States:
 *  CHECKING      — initial API call in progress
 *  NOT_LINKED    — no active external_identity mapping for the current user
 *  LINKED        — employee profile, leave, attendance, and workload visible
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  User, Mail, Briefcase, Building2, Hash,
  CalendarDays, Clock, CheckCircle2, XCircle,
  AlertCircle, RefreshCw, BookOpen, TrendingUp,
  FileText, ShieldAlert, Layers,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="h-4.5 w-4.5 text-primary" strokeWidth={1.75} />
      </div>
      <div>
        <div className="text-base font-semibold text-foreground">{title}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
}

function LeaveBalanceCard({ item }) {
  const pct = item.entitled_days > 0
    ? Math.round((item.available_days / item.entitled_days) * 100)
    : 0;
  const color = pct >= 60 ? "bg-emerald-500" : pct >= 30 ? "bg-amber-500" : "bg-red-500";
  const textColor = pct >= 60 ? "text-emerald-700" : pct >= 30 ? "text-amber-700" : "text-red-700";

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-2"
         data-testid={`leave-balance-${item.leave_type_code}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {item.leave_type_code}
        </span>
        <span className={`text-xs font-medium ${textColor}`}>{pct}%</span>
      </div>
      <div className="text-sm font-medium text-foreground leading-tight">{item.leave_type_name}</div>
      <div className="w-full h-1.5 rounded-full bg-border overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span><strong className="text-foreground">{item.available_days}d</strong> available</span>
        <span>{item.consumed_days}d used / {item.entitled_days}d total</span>
      </div>
    </div>
  );
}

const LEAVE_STATUS = {
  APPROVED:  { label: "Approved",  bg: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  SUBMITTED: { label: "Pending",   bg: "bg-amber-100 text-amber-700 border-amber-200" },
  REJECTED:  { label: "Rejected",  bg: "bg-red-100 text-red-700 border-red-200" },
  CANCELLED: { label: "Cancelled", bg: "bg-slate-100 text-slate-600 border-slate-200" },
  WITHDRAWN: { label: "Withdrawn", bg: "bg-slate-100 text-slate-600 border-slate-200" },
};

const ATT_STATUS = {
  PRESENT: { label: "Present", dot: "bg-emerald-500" },
  ABSENT:  { label: "Absent",  dot: "bg-red-500" },
  LATE:    { label: "Late",    dot: "bg-amber-500" },
  WFH:     { label: "WFH",     dot: "bg-blue-500" },
};

// ── Not-linked state ──────────────────────────────────────────────────────────

function NotLinkedView({ authEmail }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="not-linked-view">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="h-20 w-20 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
          <ShieldAlert className="h-10 w-10 text-amber-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">Employee Profile Not Linked</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Your login account (<strong>{authEmail}</strong>) has not been linked to an employee
            record in this institution. Contact your HR department to get set up.
          </p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 text-left space-y-1">
          <div className="font-semibold">What to do:</div>
          <ul className="list-disc list-inside space-y-1 text-amber-700">
            <li>Contact the HR Admin or People Operations team</li>
            <li>Provide your employee ID or worker number</li>
            <li>HR will establish your identity link</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function MySelfService() {
  const token = localStorage.getItem("academiaos_token");
  const headers = { Authorization: `Bearer ${token}` };

  const [status, setStatus]         = useState("CHECKING");   // CHECKING | NOT_LINKED | LINKED
  const [profile, setProfile]       = useState(null);
  const [leaveData, setLeaveData]   = useState(null);
  const [attendData, setAttendData] = useState(null);
  const [workload, setWorkload]     = useState(null);
  const [authEmail, setAuthEmail]   = useState("");
  const [error, setError]           = useState("");
  const [loading, setLoading]       = useState(false);

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      // Step 1: Check identity link status
      const statusRes = await axios.get(`${API}/api/v1/peo03/identity-links/me/status`, { headers });
      const statusData = statusRes.data;
      setAuthEmail(statusData.auth_email || "");

      if (!statusData.linked) {
        setStatus("NOT_LINKED");
        setLoading(false);
        return;
      }

      // Step 2: Load all self-service data in parallel
      const [profileRes, leaveRes, attendRes, workloadRes] = await Promise.allSettled([
        axios.get(`${API}/api/v1/peo03/me`, { headers }),
        axios.get(`${API}/api/v1/peo03/me/leave`, { headers }),
        axios.get(`${API}/api/v1/peo03/me/attendance`, { headers }),
        axios.get(`${API}/api/v1/peo03/me/workload`, { headers }),
      ]);

      if (profileRes.status === "fulfilled") setProfile(profileRes.value.data);
      if (leaveRes.status === "fulfilled") setLeaveData(leaveRes.value.data);
      if (attendRes.status === "fulfilled") setAttendData(attendRes.value.data);
      if (workloadRes.status === "fulfilled") setWorkload(workloadRes.value.data);

      setStatus("LINKED");
    } catch (err) {
      if (err.response?.status === 404 || err.response?.data?.detail?.code === "IDENTITY_NOT_LINKED") {
        setAuthEmail(err.response?.data?.detail?.auth_email || "");
        setStatus("NOT_LINKED");
      } else {
        const d = err.response?.data?.detail;
        setError(typeof d === "object" ? JSON.stringify(d) : (d || err.message));
        setStatus("ERROR");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  // ── Checking ──────────────────────────────────────────────────────────────
  if (status === "CHECKING" || (loading && status !== "LINKED")) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="self-service-loading">
        <div className="text-center text-muted-foreground">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-3" />
          <p className="text-sm">Loading your employee profile…</p>
        </div>
      </div>
    );
  }

  // ── Not linked ────────────────────────────────────────────────────────────
  if (status === "NOT_LINKED") {
    return <NotLinkedView authEmail={authEmail} />;
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (status === "ERROR") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="self-service-error">
        <div className="max-w-md text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
          <div className="text-lg font-semibold">Unable to load your profile</div>
          <p className="text-sm text-muted-foreground">{error}</p>
          <button onClick={loadAll} className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── Linked — full self-service ──────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background" data-testid="self-service-page">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="border-b border-border bg-card px-6 py-5">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="self-service-title">
              My Self-Service
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Your employee profile, leave, attendance and workload
            </p>
          </div>
          <button
            onClick={loadAll}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm border border-border hover:bg-muted transition"
            data-testid="self-service-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.75} />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-8">

        {/* ── Profile Card ─────────────────────────────────────────────────── */}
        {profile && (
          <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="profile-card">
            <div className="h-2 bg-gradient-to-r from-primary/80 to-primary/30" />
            <div className="p-6">
              <SectionHeader icon={User} title="My Profile" sub="Your canonical employee record" />
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { icon: User,      label: "Name",            value: profile.employee_name },
                  { icon: Hash,      label: "Worker Number",   value: profile.worker_number },
                  { icon: Building2, label: "Department",      value: profile.department_name || "—" },
                  { icon: Briefcase, label: "Job Title",       value: profile.job_title || "—" },
                  { icon: Layers,    label: "Employment Type", value: profile.employment_type || "—" },
                  { icon: Mail,      label: "Auth Email",      value: profile.auth_email },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-start gap-2.5">
                    <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.75} />
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground/60">{label}</div>
                      <div className="text-sm font-medium text-foreground">{value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Leave ─────────────────────────────────────────────────────────── */}
        {leaveData && (
          <section data-testid="self-service-leave-section">
            <SectionHeader icon={CalendarDays} title="My Leave" sub="Annual entitlements and recent requests" />

            {/* Balance grid */}
            {leaveData.balance?.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-testid="leave-balance-grid">
                {leaveData.balance.map(b => (
                  <LeaveBalanceCard key={b.leave_type_code} item={b} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-6">No leave entitlements configured.</p>
            )}

            {/* Recent requests */}
            {leaveData.recent_requests?.length > 0 && (
              <div className="rounded-xl border border-border overflow-hidden" data-testid="recent-leave-requests">
                <div className="px-4 py-3 bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Recent Leave Requests
                </div>
                <div className="divide-y divide-border">
                  {leaveData.recent_requests.map(req => {
                    const cfg = LEAVE_STATUS[req.status] || { label: req.status, bg: "bg-slate-100 text-slate-600 border-slate-200" };
                    return (
                      <div key={req.id} className="px-4 py-3 flex items-center justify-between gap-2"
                           data-testid={`leave-request-${req.id}`}>
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            {req.leave_type_name}{" "}
                            <span className="text-xs text-muted-foreground">({req.leave_type_code})</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {fmtDate(req.from_date)} – {fmtDate(req.to_date)} · {req.requested_days}d
                          </div>
                          {req.reason && (
                            <div className="text-xs text-muted-foreground/60 mt-0.5 truncate max-w-[300px]"
                                 title={req.reason}>"{req.reason}"</div>
                          )}
                        </div>
                        <span className={`shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.bg}`}>
                          {cfg.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        )}

        {/* ── Attendance ────────────────────────────────────────────────────── */}
        {attendData && (
          <section data-testid="self-service-attendance-section">
            <SectionHeader icon={Clock} title="My Attendance" sub="Summary of recorded attendance" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                { label: "Present Days",   value: attendData.present_days,     color: "emerald" },
                { label: "Absent Days",    value: attendData.absent_days,      color: "red" },
                { label: "Exception Days", value: attendData.exception_days,   color: "amber" },
                { label: "Hours Worked",   value: `${attendData.total_worked_hours}h`, color: "blue" },
              ].map(({ label, value, color }) => {
                const colors = {
                  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
                  red:     "bg-red-50 border-red-200 text-red-700",
                  amber:   "bg-amber-50 border-amber-200 text-amber-700",
                  blue:    "bg-blue-50 border-blue-200 text-blue-700",
                };
                return (
                  <div key={label} className={`rounded-xl border p-3 text-center ${colors[color]}`}
                       data-testid={`att-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div className="text-xl font-bold">{value}</div>
                    <div className="text-xs opacity-70 mt-0.5">{label}</div>
                  </div>
                );
              })}
            </div>

            {attendData.recent_days?.length > 0 && (
              <div className="rounded-xl border border-border overflow-hidden" data-testid="recent-attendance">
                <div className="px-4 py-2.5 bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Last 7 Days
                </div>
                <div className="divide-y divide-border">
                  {attendData.recent_days.map((d, i) => {
                    const cfg = ATT_STATUS[d.status] || { label: d.status, dot: "bg-slate-400" };
                    return (
                      <div key={i} className="px-4 py-2.5 flex items-center justify-between text-sm"
                           data-testid={`att-day-${d.date}`}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${cfg.dot} shrink-0`} />
                          <span className="text-foreground">{fmtDate(d.date)}</span>
                        </div>
                        <div className="flex items-center gap-4 text-muted-foreground text-xs">
                          <span>{cfg.label}</span>
                          <span>{d.worked_hours}h</span>
                          {d.has_exception && (
                            <span className="text-amber-600 font-medium flex items-center gap-0.5">
                              <AlertCircle className="h-3 w-3" /> Exception
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        )}

        {/* ── Workload ─────────────────────────────────────────────────────── */}
        {workload && workload.sections?.length > 0 && (
          <section data-testid="self-service-workload-section">
            <SectionHeader
              icon={BookOpen}
              title="My Teaching Workload"
              sub={`${workload.total_sections} sections · ${workload.total_contact_hours}h contact hours`}
            />
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm" data-testid="workload-table">
                  <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-2.5">Course</th>
                      <th className="text-left px-4 py-2.5">Section</th>
                      <th className="text-left px-4 py-2.5">Term</th>
                      <th className="text-left px-4 py-2.5">Mode</th>
                      <th className="text-left px-4 py-2.5">Schedule</th>
                      <th className="text-right px-4 py-2.5">Hrs/wk</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {workload.sections.map(s => (
                      <tr key={s.section_id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-foreground">{s.course_code}</div>
                          <div className="text-xs text-muted-foreground">{s.course_title}</div>
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{s.section_code}</td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">{s.term_code}</td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">{s.delivery_mode || "—"}</td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">{s.schedule_block || "—"}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-foreground">{s.weekly_contact_hours}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="sm:hidden divide-y divide-border">
                {workload.sections.map(s => (
                  <div key={s.section_id} className="p-4 space-y-1">
                    <div className="font-medium text-foreground">{s.course_code} — {s.course_title}</div>
                    <div className="text-xs text-muted-foreground">{s.section_code} · {s.term_code} · {s.weekly_contact_hours}h/wk</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* No workload (non-faculty) */}
        {workload && workload.sections?.length === 0 && (
          <section data-testid="no-workload-section">
            <SectionHeader icon={BookOpen} title="My Teaching Workload" />
            <div className="rounded-xl border border-border bg-card text-center py-10 text-muted-foreground text-sm">
              No section assignments found for your record.
            </div>
          </section>
        )}

      </div>
    </div>
  );
}
